import { useT, type Clave } from '../i18n';
import { Desplegable } from './ui';

/**
 * La Character Bible, entera y editable.
 *
 * ── Por qué es un componente y no un formulario dentro de una pantalla ──────────
 * La misma ficha se escribe en dos momentos: al crear la influencer, antes de gastar una sola
 * imagen, y después desde su detalle. Escrita dos veces, las dos versiones divergen — y ya pasó
 * con menos: el detalle mostraba las afirmaciones prohibidas como una lista de sólo lectura
 * mientras el resto de la pantalla se editaba, sin ningún motivo salvo que nadie las había
 * cableado.
 *
 * ── Por qué las etiquetas están traducidas y los valores no ─────────────────────
 * «skinTone», «faceShape», «distinguishingFeatures»: el panel mostraba las claves crudas del
 * JSON. Son nombres de campo de un esquema, no palabras de un idioma, y quien lee la ficha de su
 * influencer no tiene por qué saber inglés ni conocer el esquema.
 *
 * Los valores son harina de otro costal: los escribe el modelo en el idioma que se le pidió, y
 * traducirlos al vuelo reescribiría la identidad cada vez que alguien toca el selector.
 *
 * ── Los tres campos que se quedan en inglés ─────────────────────────────────────
 * `basePrompt`, `negativePrompt` y `photographyStyle` los lee el modelo de imagen, que trabaja
 * en inglés. Traducirlos empeora el resultado. Se marcan con una insignia y se explica por qué,
 * porque un campo en otro idioma en medio de una ficha en español parece un error — y quien lo
 * «arregla» traduciéndolo degrada sus propias imágenes sin enterarse.
 */

export type IdentidadVisual = {
  skinTone: string;
  faceShape: string;
  eyeColor: string;
  hairColor: string;
  hairStyle: string;
  bodyDescription: string;
  distinguishingFeatures: string[];
  defaultWardrobe: string[];
  colorPalette: string[];
  photographyStyle: string;
};

export type Ficha = {
  publicName: string;
  fictionalAge: number;
  birthday: string;
  countryNarrative: string;
  languages: string[];
  niche: string[];
  personalityTraits: string[];
  backstory: string;
  values: string[];
  interests: string[];
  toneOfVoice: string;
  catchphrases: string[];
  prohibitedClaims: string[];
  visualIdentity: IdentidadVisual;
  disclosureText: string;
  basePrompt: string;
  bodyPrompt: string;
  negativePrompt: string;
};

/** Los campos de texto simple de la identidad visual, en el orden en que se leen. */
const CAMPOS_VISUALES = [
  'skinTone',
  'faceShape',
  'eyeColor',
  'hairColor',
  'hairStyle',
  'bodyDescription',
] as const;

/** Los que son listas. Se editan uno por línea: una lista no entra en un campo de texto. */
const LISTAS_VISUALES = ['distinguishingFeatures', 'defaultWardrobe', 'colorPalette'] as const;

export function EditorDeFicha({
  ficha,
  onChange,
}: {
  ficha: Ficha;
  onChange: (ficha: Ficha) => void;
}) {
  const { t } = useT();

  const set = <C extends keyof Ficha>(campo: C, valor: Ficha[C]): void =>
    onChange({ ...ficha, [campo]: valor });

  const setVisual = <C extends keyof IdentidadVisual>(campo: C, valor: IdentidadVisual[C]): void =>
    onChange({ ...ficha, visualIdentity: { ...ficha.visualIdentity, [campo]: valor } });

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <Texto
          clave="ficha.publicName"
          valor={ficha.publicName}
          onChange={(valor) => set('publicName', valor)}
        />
        <Numero
          clave="ficha.fictionalAge"
          valor={ficha.fictionalAge}
          onChange={(valor) => set('fictionalAge', valor)}
        />
        <Texto
          clave="ficha.birthday"
          valor={ficha.birthday}
          onChange={(valor) => set('birthday', valor)}
        />
        <Texto
          clave="ficha.countryNarrative"
          valor={ficha.countryNarrative}
          onChange={(valor) => set('countryNarrative', valor)}
        />
      </div>

      <Lista clave="ficha.niche" valores={ficha.niche} onChange={(v) => set('niche', v)} />
      <Lista
        clave="ficha.languages"
        valores={ficha.languages}
        onChange={(v) => set('languages', v)}
      />

      <Parrafo
        clave="ficha.backstory"
        valor={ficha.backstory}
        filas={6}
        onChange={(valor) => set('backstory', valor)}
      />
      <Texto
        clave="ficha.toneOfVoice"
        valor={ficha.toneOfVoice}
        onChange={(valor) => set('toneOfVoice', valor)}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <Lista
          clave="ficha.personalityTraits"
          valores={ficha.personalityTraits}
          onChange={(v) => set('personalityTraits', v)}
        />
        <Lista clave="ficha.values" valores={ficha.values} onChange={(v) => set('values', v)} />
        <Lista
          clave="ficha.interests"
          valores={ficha.interests}
          onChange={(v) => set('interests', v)}
        />
        <Lista
          clave="ficha.catchphrases"
          valores={ficha.catchphrases}
          onChange={(v) => set('catchphrases', v)}
        />
      </div>

      {/*
        Las afirmaciones prohibidas se editan como todo lo demás.

        Se mostraban en una lista de sólo lectura, y son justamente lo que uno querría ajustar:
        son lo único de la ficha que restringe qué puede decir la influencer en cada publicación
        que se escriba de acá en adelante.
      */}
      <Lista
        clave="ficha.prohibitedClaims"
        ayuda="ficha.prohibitedClaimsAyuda"
        valores={ficha.prohibitedClaims}
        onChange={(v) => set('prohibitedClaims', v)}
      />

      <div className="rounded-2xl border border-[var(--color-border-subtle)] p-4">
        <p className="text-sm font-medium">{t('ficha.visualIdentity')}</p>

        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          {CAMPOS_VISUALES.map((campo) => (
            <Texto
              key={campo}
              clave={`ficha.${campo}` as Clave}
              valor={ficha.visualIdentity[campo]}
              onChange={(valor) => setVisual(campo, valor)}
            />
          ))}
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {LISTAS_VISUALES.map((campo) => (
            <Lista
              key={campo}
              clave={`ficha.${campo}` as Clave}
              valores={ficha.visualIdentity[campo]}
              onChange={(valores) => setVisual(campo, valores)}
            />
          ))}
        </div>
      </div>

      {/*
        Los cuatro campos del generador, juntos y plegados.

        ── Por qué separados ────────────────────────────────────────────────────
        Estaban intercalados entre Historia, Valores y Muletillas, con una insignia chiquita que
        decía «En inglés». Quien no sabe inglés veía cuatro campos incomprensibles en medio de la
        ficha de su influencer y razonablemente concluía que algo había salido mal.

        El error era tratarlos como campos de la ficha. No lo son: **la ficha describe a la
        persona; estos le dicen a una máquina qué dibujar**. Son de otra naturaleza y merecen
        otro lugar.

        Plegados porque tienen un valor por defecto que funciona: quien no va a afinar el prompt
        no necesita verlos nunca, y quien sí, los tiene enteros a un click.
      */}
      <Desplegable titulo={t('ficha.paraElGenerador')} detalle={t('ficha.paraElGeneradorDetalle')}>
        <div className="space-y-4">
          <p className="text-xs text-[var(--color-ink-muted)]">
            {t('ficha.paraElGeneradorPorQue')}
          </p>

          <Parrafo
            clave="ficha.basePrompt"
            valor={ficha.basePrompt}
            filas={4}
            onChange={(valor) => set('basePrompt', valor)}
          />
          <Parrafo
            clave="ficha.bodyPrompt"
            valor={ficha.bodyPrompt ?? ''}
            filas={2}
            ayuda="ficha.bodyPromptAyuda"
            onChange={(valor) => set('bodyPrompt', valor)}
          />
          <Parrafo
            clave="ficha.photographyStyle"
            valor={ficha.visualIdentity.photographyStyle}
            filas={3}
            onChange={(valor) => setVisual('photographyStyle', valor)}
          />
          <Parrafo
            clave="ficha.negativePrompt"
            valor={ficha.negativePrompt}
            filas={2}
            onChange={(valor) => set('negativePrompt', valor)}
          />
        </div>
      </Desplegable>
    </div>
  );
}

// ── Campos ──────────────────────────────────────────────────────────────────────

const CLASE_CAMPO =
  'mt-1 w-full rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] px-3 py-2 text-sm';

/**
 * El nombre de un campo.
 *
 * Llevaba una insignia «En inglés» por campo. Dejó de hacer falta cuando los cuatro que lo
 * están se agruparon en su propia sección, cuyo título ya lo dice: repetirlo en cada uno era
 * decir cuatro veces lo mismo dentro de un recuadro que lo decía una.
 */
function Etiqueta({ clave }: { clave: Clave }) {
  const { t } = useT();
  return <span className="flex items-center gap-2 text-xs font-medium">{t(clave)}</span>;
}

function Texto({
  clave,
  valor,
  onChange,
}: {
  clave: Clave;
  valor: string;
  onChange: (valor: string) => void;
}) {
  return (
    <label className="block">
      <Etiqueta clave={clave} />
      <input
        type="text"
        value={valor}
        onChange={(event) => onChange(event.target.value)}
        className={CLASE_CAMPO}
      />
    </label>
  );
}

function Numero({
  clave,
  valor,
  onChange,
}: {
  clave: Clave;
  valor: number;
  onChange: (valor: number) => void;
}) {
  return (
    <label className="block">
      <Etiqueta clave={clave} />
      <input
        type="number"
        value={valor}
        onChange={(event) => onChange(Number(event.target.value))}
        className={CLASE_CAMPO}
      />
    </label>
  );
}

function Parrafo({
  clave,
  valor,
  filas,
  ayuda,
  onChange,
}: {
  clave: Clave;
  valor: string;
  filas: number;
  ayuda?: Clave;
  onChange: (valor: string) => void;
}) {
  const { t } = useT();

  return (
    <label className="block">
      <Etiqueta clave={clave} />
      {ayuda && <p className="mt-0.5 text-xs text-[var(--color-ink-muted)]">{t(ayuda)}</p>}
      <textarea
        value={valor}
        rows={filas}
        onChange={(event) => onChange(event.target.value)}
        className={CLASE_CAMPO}
      />
    </label>
  );
}

/**
 * Una lista, editada como texto con un elemento por línea.
 *
 * ── Por qué una línea por elemento y no chips con una crucecita ─────────────────
 * Chips se ven mejor y se editan peor: para corregir una falta de ortografía hay que borrar el
 * elemento y volver a escribirlo entero. Acá se edita en el lugar, se reordena moviendo líneas y
 * se pega una lista desde otro lado de un tirón.
 *
 * Se filtran las líneas vacías al salir, no al escribir: filtrarlas mientras alguien tipea le
 * borra el Enter que acaba de pulsar para empezar el elemento siguiente.
 */
function Lista({
  clave,
  ayuda,
  valores,
  onChange,
}: {
  clave: Clave;
  ayuda?: Clave;
  valores: string[];
  onChange: (valores: string[]) => void;
}) {
  const { t } = useT();

  return (
    <label className="block">
      <Etiqueta clave={clave} />
      {ayuda && <p className="mt-0.5 text-xs text-[var(--color-ink-muted)]">{t(ayuda)}</p>}
      <textarea
        value={valores.join('\n')}
        rows={Math.min(Math.max(valores.length, 2), 8)}
        placeholder={t('ficha.listaSeparada')}
        onChange={(event) => onChange(event.target.value.split('\n'))}
        onBlur={(event) =>
          onChange(
            event.target.value
              .split('\n')
              .map((linea) => linea.trim())
              .filter(Boolean),
          )
        }
        className={CLASE_CAMPO}
      />
    </label>
  );
}
